import { useCallback, useMemo } from "react";
import { Crew } from "../context/CrewContext";
import { createAssociateSearchFilter, sortAssociatesByName } from "../utils/associateFilters";

export type ResolvedCrewMember = {
  uid: string;
  first: string;
  last: string;
  company: string;
  avatar: string;
  found: boolean;
};

type AnyAssociate = any;

export function useCrewMemberEditor(args: {
  myAssociates: AnyAssociate[] | undefined;
  selectedCrew: Crew | null;
  memberSearchText: string;
  showSelectedOnly: boolean;
  selectedMemberIds: string[];
  setSelectedMemberIds: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const {
    myAssociates,
    selectedCrew,
    memberSearchText,
    showSelectedOnly,
    selectedMemberIds,
    setSelectedMemberIds,
  } = args;

  // Crew Details Member List /////////////////////////////////////
  const crewMemberIds = useMemo(() => {
    const ids = Array.isArray(selectedCrew?.memberIds)
      ? (selectedCrew?.memberIds as any[])
      : [];
    return ids.filter((v) => typeof v === "string" && v.length > 0) as string[];
  }, [selectedCrew?.memberIds]);

  const crewMembersResolved = useMemo<ResolvedCrewMember[]>(() => {
    const assocList = myAssociates || [];
    const byUid = new Map<string, AnyAssociate>();

    assocList.forEach((a: AnyAssociate) => {
      const uid = a?.WerkerUid;
      if (typeof uid === "string" && uid.length > 0) byUid.set(uid, a);
    });

    return crewMemberIds.map((uid) => {
      const a = byUid.get(uid);
      return {
        uid,
        first: a?.WerkerFirstName || "",
        last: a?.WerkerLastName || "",
        company: a?.WerkerCompany || "",
        avatar: a?.WerkerProfilePicURL || "",
        found: !!a,
      };
    });
  }, [crewMemberIds, myAssociates]);

  // EDIT MEMBERS: Filter + Select All (SelectWerkers-style) ///////
  const baseMembersList = useMemo(() => {
    const list = myAssociates || [];
    if (!showSelectedOnly) return list;

    const selectedSet = new Set(selectedMemberIds);
    return list.filter((a: AnyAssociate) => selectedSet.has(a?.WerkerUid));
  }, [myAssociates, showSelectedOnly, selectedMemberIds]);

  const memberSearchLower = useMemo(() => {
    return (memberSearchText || "").toLowerCase().trim();
  }, [memberSearchText]);

  const filteredSortedMembers = useMemo(() => {
    const base = (baseMembersList || []).filter((a: AnyAssociate) => !!a?.WerkerUid);

    const filtered = !memberSearchLower
      ? base
      : base.filter(createAssociateSearchFilter(memberSearchText));

    return sortAssociatesByName(filtered);
  }, [baseMembersList, memberSearchLower, memberSearchText]);

  const visibleUids = useMemo(() => {
    const uids = (filteredSortedMembers || [])
      .map((a: AnyAssociate) => a?.WerkerUid)
      .filter((v: any) => typeof v === "string" && v.length > 0) as string[];

    return Array.from(new Set(uids));
  }, [filteredSortedMembers]);

  const allVisibleSelected = useMemo(() => {
    if (visibleUids.length === 0) return false;
    const set = new Set(selectedMemberIds);
    return visibleUids.every((id: string) => set.has(id));
  }, [visibleUids, selectedMemberIds]);

  const someVisibleSelected = useMemo(() => {
    if (visibleUids.length === 0) return false;
    const set = new Set(selectedMemberIds);
    return visibleUids.some((id: string) => set.has(id));
  }, [visibleUids, selectedMemberIds]);

  const visibleSelectedCount = useMemo(() => {
    if (visibleUids.length === 0) return 0;
    const set = new Set(selectedMemberIds);
    let count = 0;
    visibleUids.forEach((id: string) => {
      if (set.has(id)) count += 1;
    });
    return count;
  }, [visibleUids, selectedMemberIds]);

  // Row click ONLY toggles. Filter changes never change selectedMemberIds.
  const toggleSelectAllVisible = useCallback(() => {
    if (visibleUids.length === 0) return;

    setSelectedMemberIds((prev) => {
      const set = new Set(prev);

      if (allVisibleSelected) {
        // clear ONLY visible
        visibleUids.forEach((id) => set.delete(id));
      } else {
        // select ONLY visible
        visibleUids.forEach((id) => set.add(id));
      }

      return Array.from(set);
    });
  }, [visibleUids, allVisibleSelected, setSelectedMemberIds]);

  return {
    crewMembersResolved,

    memberSearchLower,
    filteredSortedMembers,
    visibleUids,
    allVisibleSelected,
    someVisibleSelected,
    visibleSelectedCount,
    toggleSelectAllVisible,
  };
}
